import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Check, X, Tags, AlertTriangle, Loader2 } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusRow = ({ item, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description || '');

  const cancel = () => { setEditing(false); setName(item.name); setDescription(item.description || ''); };
  const save = async () => { if (await onSave(item, name.trim(), description.trim())) setEditing(false); };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(160px,1fr)_2fr_120px_auto] gap-2 md:gap-4 items-center px-5 py-3 border-b last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`company-status-row-${item.id}`}>
      {editing ? (
        <>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`company-status-name-input-${item.id}`} />
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Bu statünün anlamı..." data-testid={`company-status-desc-input-${item.id}`} />
        </>
      ) : (
        <>
          <span className="font-medium text-slate-800">{item.name}</span>
          <span className="text-sm text-slate-500">{item.description || <span className="italic text-slate-300">Açıklama yok</span>}</span>
        </>
      )}
      <Badge variant="outline" className="justify-center" data-testid={`company-status-count-${item.id}`}>{item.company_count} firma</Badge>
      <div className="flex gap-1 justify-end">
        {editing ? (
          <>
            <Button size="icon" variant="ghost" className="text-emerald-600" onClick={save} disabled={!name.trim()} data-testid={`company-status-save-${item.id}`}><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={cancel} data-testid={`company-status-cancel-${item.id}`}><X className="h-4 w-4" /></Button>
          </>
        ) : (
          <>
            <Button size="icon" variant="ghost" className="text-blue-600" onClick={() => setEditing(true)} data-testid={`company-status-edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => onDelete(item)} data-testid={`company-status-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
          </>
        )}
      </div>
    </div>
  );
};

export default function CompanyStatusesPage() {
  const { user, canView, canTab } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/company-statuses`);
      setItems(res.data);
    } catch {
      toast.error('Firma statüleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const errorText = (err) => (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'İşlem başarısız');

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/company-statuses`, { name: newName.trim(), description: newDesc.trim() });
      toast.success('Firma statüsü eklendi');
      setNewName(''); setNewDesc('');
      load();
    } catch (err) { toast.error(errorText(err)); }
  };

  const doUpdate = async (item, name, description) => {
    try {
      await axios.put(`${API}/company-statuses/${item.id}`, { name, description });
      toast.success(item.company_count > 0 && name !== item.name ? `${item.company_count} firmanın statüsü "${name}" olarak güncellendi` : 'Firma statüsü güncellendi');
      load();
      return true;
    } catch (err) { toast.error(errorText(err)); return false; }
  };

  // İsim değişiyor ve kayıtlı firma varsa önce uyarı göster
  const handleSave = (item, name, description) => {
    if (item.company_count > 0 && name !== item.name) {
      return new Promise((resolve) => setConfirm({ item, name, description, resolve }));
    }
    return doUpdate(item, name, description);
  };

  const handleDelete = async (item) => {
    if (item.company_count > 0) {
      toast.error(`Bu statüde ${item.company_count} firma kayıtlı. Silmeden önce bu firmaların statüsünü değiştirin.`);
      return;
    }
    if (!(await confirmDialog({ title: 'Statü silinsin mi?', description: `"${item.name}" statüsü silinsin mi?`, confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/company-statuses/${item.id}`);
      toast.success('Firma statüsü silindi');
      load();
    } catch (err) { toast.error(errorText(err)); }
  };

  const closeConfirm = async (accepted) => {
    const c = confirm;
    setConfirm(null);
    c.resolve(accepted ? await doUpdate(c.item, c.name, c.description) : false);
  };

  if (!canView('company_statuses')) {
    return <p className="text-slate-500" data-testid="company-statuses-forbidden">Bu sayfa sadece Süper Admin kullanıcılar içindir.</p>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="company-statuses-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Tags className="h-6 w-6" />Firma Statüsü</h1>
        <p className="text-slate-500 mt-1">Firmalara atanabilecek statüleri ve anlamlarını yönetin. Buradaki değişiklikler Firmalar sayfasına otomatik yansır.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <p className="text-sm font-medium text-slate-700 mb-3">Yeni Firma Statüsü</p>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(160px,1fr)_2fr_auto] gap-3">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Statü adı (ör. Taşeron)" data-testid="new-company-status-name" />
            <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Açıklama: bu statünün anlamı nedir?" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="new-company-status-desc" />
            <Button onClick={add} disabled={!newName.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="add-company-status-btn"><Plus className="h-4 w-4 mr-1" />Ekle</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="hidden md:grid grid-cols-[minmax(160px,1fr)_2fr_120px_auto] gap-4 px-5 py-3 bg-slate-50 border-b text-sm font-semibold text-slate-600">
            <span>Statü</span><span>Açıklama</span><span className="text-center">Kayıtlı</span><span className="w-20" />
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-400 py-10">Henüz firma statüsü yok</p>
          ) : (
            items.map(item => <StatusRow key={`${item.id}-${item.name}-${item.description}`} item={item} onSave={handleSave} onDelete={handleDelete} />)
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && confirm && closeConfirm(false)}>
        <AlertDialogContent data-testid="company-status-rename-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Statü adı değişecek</AlertDialogTitle>
            <AlertDialogDescription>
              Bu statüde <strong>{confirm?.item.company_count} firma</strong> kayıtlıdır, tümü değişecek.
              <br />"{confirm?.item.name}" → "{confirm?.name}" olarak güncellensin mi?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => closeConfirm(false)} data-testid="company-status-rename-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={() => closeConfirm(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="company-status-rename-confirm">Evet, Değiştir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
